/**
 * Selector de categoría: buscar escribiendo, recientes, agrupadas (variables, fijos, ingresos, no cuentan),
 * sugerencias de un toque y «＋ Crear…». Se monta solo encima de cualquier <select data-cat-picker>:
 * el <select> sigue ahí (oculto) y es quien guarda el valor y avisa con su evento «change», así que las pantallas
 * no cambian. En el móvil se abre como una hoja desde abajo; en el ordenador, debajo del campo.
 *   data-suggest="A|B|C"  sugerencias para ese movimiento (solo se enseñan mientras no tiene categoría)
 *   option value="__new__" en el select  permite «＋ Crear «…»» (la pantalla recoge el nombre con takeNewCategoryName)
 */
import { GROUP_ORDER, groupOf, type CategoryGroup } from '../app/category-suggest.js';
import type { Category } from '@pf/domain';

const NEW = '__new__';
let meta = new Map<string, CategoryGroup>();
let recents: string[] = [];
let pendingNew: string | null = null;

/** Grupos y recientes del libro (se actualiza con cada cálculo). */
export function setCategoryMeta(categories: Category[], recent: string[]) {
  meta = new Map(categories.map(c => [c.name.toLowerCase(), groupOf(c)]));
  recents = recent;
}
/** Tras cambiar el valor del select desde código (sin evento): que el selector enseñe el nuevo. */
export function syncCategoryPicker(select: HTMLSelectElement) { syncs.get(select)?.(); }
/** El nombre escrito en «＋ Crear «…»» (una sola vez); null si se eligió «Nueva categoría…» sin escribir. */
export function takeNewCategoryName(): string | null { const n = pendingNew; pendingNew = null; return n; }

const norm = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
const esc = (s: string) => s.replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]!));
const mark = (name: string, q: string) => {
  const i = q ? norm(name).indexOf(norm(q)) : -1;
  return i < 0 ? esc(name) : `${esc(name.slice(0, i))}<mark>${esc(name.slice(i, i + q.length))}</mark>${esc(name.slice(i + q.length))}`;
};
const phone = () => matchMedia('(max-width: 720px)').matches;
const CHEV = '<svg class="cp-chev" viewBox="0 0 24 24" aria-hidden="true"><path d="M6 9l6 6 6-6"/></svg>';

/* ---------- Montaje sobre los <select data-cat-picker> ---------- */

function enhance(select: HTMLSelectElement) {
  select.dataset.cp = '1';
  select.classList.add('cp-native');
  select.tabIndex = -1;
  select.setAttribute('aria-hidden', 'true');
  const trigger = document.createElement('button');
  trigger.type = 'button';
  trigger.className = 'cp-trigger';
  trigger.setAttribute('aria-haspopup', 'listbox');
  if (select.getAttribute('aria-label')) trigger.setAttribute('aria-label', select.getAttribute('aria-label')!);
  if (select.id) { const label = document.querySelector<HTMLLabelElement>(`label[for="${CSS.escape(select.id)}"]`); if (label) { trigger.id = `${select.id}-cp`; label.htmlFor = trigger.id; } }
  select.after(trigger);
  const chips = document.createElement('div');
  chips.className = 'cp-chips';
  select.parentElement!.append(chips);
  const sync = () => {
    const o = select.selectedOptions[0];
    trigger.innerHTML = `<span class="cp-val${select.value ? '' : ' cp-none-picked'}">${esc(o?.text ?? '')}</span>${CHEV}`;
    const sug = (select.dataset.suggest ?? '').split('|').filter(Boolean);
    chips.hidden = !!select.value || !sug.length;
    chips.innerHTML = chips.hidden ? '' : sug.map(n => `<button type="button" class="cp-chip" data-n="${esc(n)}">${esc(n)}</button>`).join('');
  };
  sync();
  syncs.set(select, sync);
  select.addEventListener('change', sync);
  trigger.onclick = () => open(select, trigger);
  chips.onclick = e => { const b = (e.target as HTMLElement).closest<HTMLElement>('.cp-chip'); if (b) choose(select, b.dataset.n!); };
}

/** Pone el valor en el select (creando la opción si hace falta) y avisa a la pantalla como si lo hubiera elegido allí. */
function choose(select: HTMLSelectElement, value: string, create?: string) {
  if (create) { pendingNew = create; select.value = NEW; }
  else {
    if (![...select.options].some(o => o.value === value)) select.add(new Option(value, value));
    select.value = value;
  }
  select.dispatchEvent(new Event('change', { bubbles: true }));
  syncs.get(select)?.();   // la pantalla puede haber cambiado el valor al recibir el aviso (p. ej. al crear la categoría)
}
const syncs = new WeakMap<HTMLSelectElement, () => void>();

let observer: MutationObserver | null = null;
/** Vigila la pantalla: cada <select data-cat-picker> nuevo recibe su selector. */
export function startCategoryPickers(root: HTMLElement = document.body) {
  const run = () => root.querySelectorAll<HTMLSelectElement>('select[data-cat-picker]:not([data-cp])').forEach(enhance);
  run();
  observer?.disconnect();
  observer = new MutationObserver(() => run());
  observer.observe(root, { childList: true, subtree: true });
}

/* ---------- La lista ---------- */

interface Item { value: string; html: string; create?: string }

function open(select: HTMLSelectElement, trigger: HTMLElement) {
  close();
  const options = [...select.options].filter(o => o.value !== NEW);
  const empty = options.find(o => o.value === '');
  const names = options.filter(o => o.value !== '').map(o => o.value);
  const canCreate = [...select.options].some(o => o.value === NEW);
  const backdrop = document.createElement('div');
  backdrop.className = 'cp-backdrop';
  const panel = document.createElement('div');
  panel.className = `cp-panel ${phone() ? 'cp-sheet' : 'cp-pop'}`;
  panel.setAttribute('role', 'dialog');
  panel.setAttribute('aria-label', 'Elegir categoría');
  panel.innerHTML = `${phone() ? '<div class="cp-grab" aria-hidden="true"></div>' : ''}
    <input class="cp-q" type="search" role="combobox" aria-expanded="true" aria-controls="cpList" aria-autocomplete="list" autocomplete="off" enterkeyhint="done"
      placeholder="${canCreate ? 'Buscar o crear categoría…' : 'Buscar categoría…'}">
    <div class="cp-list" id="cpList" role="listbox"></div>`;
  document.body.append(backdrop, panel);
  trigger.setAttribute('aria-expanded', 'true');
  if (!phone()) {
    const r = trigger.getBoundingClientRect();
    const width = Math.max(r.width, 300);
    panel.style.width = `${width}px`;
    panel.style.left = `${Math.min(r.left, innerWidth - width - 8) + scrollX}px`;
    // Hueco libre debajo (sin la barra de pestañas) y encima; se abre donde quepa más, con la altura que haya
    const tabbar = document.querySelector<HTMLElement>('.tabbar, nav.tabs, #tabs')?.getBoundingClientRect().height ?? 0;
    const below = innerHeight - tabbar - r.bottom - 12, above = r.top - 12;
    if (below < 260 && above > below) { panel.style.top = `${r.top + scrollY - 6}px`; panel.style.transform = 'translateY(-100%)'; panel.style.maxHeight = `${Math.min(360, above)}px`; }
    else { panel.style.top = `${r.bottom + scrollY + 6}px`; panel.style.maxHeight = `${Math.min(360, Math.max(160, below))}px`; }
  } else document.body.classList.add('cp-open');
  const q = panel.querySelector<HTMLInputElement>('.cp-q')!;
  const list = panel.querySelector<HTMLElement>('.cp-list')!;
  let items: Item[] = [];
  let hi = 0;

  const draw = () => {
    const text = q.value.trim();
    const match = (n: string) => !text || norm(n).includes(norm(text));
    items = [];
    let html = '';
    const section = (label: string, values: string[]) => {
      if (!values.length) return;
      html += `<div class="cp-grp" role="presentation">${esc(label)}</div>`;
      values.forEach(v => {
        const i = items.push({ value: v, html: '' }) - 1;
        html += `<div class="cp-opt${v === select.value ? ' sel' : ''}" role="option" id="cp-${i}" data-i="${i}" aria-selected="${v === select.value}"><span>${mark(v, text)}</span>${v === select.value ? '<span class="cp-tick">✓</span>' : ''}</div>`;
      });
    };
    if (empty && (!text || match(empty.text))) {
      const i = items.push({ value: '', html: '' }) - 1;
      html += `<div class="cp-opt cp-empty${select.value === '' ? ' sel' : ''}" role="option" id="cp-${i}" data-i="${i}" aria-selected="${select.value === ''}">${esc(empty.text)}</div>`;
    }
    if (!text) section('Recientes', recents.filter(r => names.includes(r)).slice(0, 5));
    const byGroup = new Map<string, string[]>();
    names.filter(match).forEach(n => { const g = meta.get(n.toLowerCase()) ?? 'Otras'; byGroup.set(g, [...(byGroup.get(g) ?? []), n]); });
    [...GROUP_ORDER, 'Otras'].forEach(g => section(g, byGroup.get(g) ?? []));
    if (canCreate && text && !names.some(n => norm(n) === norm(text))) {
      const i = items.push({ value: NEW, html: '', create: text.replace(/^[=+\-@]+/, '').slice(0, 40) }) - 1;
      html += `<div class="cp-opt cp-new" role="option" id="cp-${i}" data-i="${i}">＋ Crear «${esc(text)}»</div>`;
    }
    if (!items.length) html = '<div class="cp-none">No hay ninguna con ese nombre</div>';
    list.innerHTML = html;
    hi = Math.min(hi, Math.max(items.length - 1, 0));
    highlight();
  };
  const highlight = () => {
    list.querySelectorAll('.cp-opt').forEach(e => e.classList.toggle('hi', Number((e as HTMLElement).dataset.i) === hi));
    q.setAttribute('aria-activedescendant', `cp-${hi}`);
    list.querySelector('.cp-opt.hi')?.scrollIntoView({ block: 'nearest' });
  };
  const pick = (i: number) => {
    const it = items[i];
    if (!it) return;
    close();
    if (it.create) choose(select, NEW, it.create); else choose(select, it.value);
    trigger.focus({ preventScroll: true });
  };
  q.oninput = () => { hi = 0; draw(); };
  q.onkeydown = e => {
    if (e.key === 'ArrowDown') { hi = Math.min(hi + 1, items.length - 1); highlight(); e.preventDefault(); }
    else if (e.key === 'ArrowUp') { hi = Math.max(hi - 1, 0); highlight(); e.preventDefault(); }
    else if (e.key === 'Enter') { e.preventDefault(); pick(hi); }
    else if (e.key === 'Escape' || e.key === 'Tab') { close(); trigger.focus({ preventScroll: true }); }
  };
  // Al tocar una opción: pointerdown para que el teclado del móvil no se cierre antes y mueva la lista
  list.onpointerdown = e => { const o = (e.target as HTMLElement).closest<HTMLElement>('.cp-opt'); if (o) { e.preventDefault(); pick(Number(o.dataset.i)); } };
  backdrop.onpointerdown = e => { e.preventDefault(); close(); };
  draw();
  // En el ordenador, la seleccionada visible al abrir; en ambos, el buscador listo para escribir
  hi = Math.max(0, items.findIndex(it => it.value === select.value && it.value !== ''));
  highlight();
  q.focus({ preventScroll: true });
  current = { panel, backdrop, trigger };
}

let current: { panel: HTMLElement; backdrop: HTMLElement; trigger: HTMLElement } | null = null;
function close() {
  if (!current) return;
  current.panel.remove(); current.backdrop.remove();
  current.trigger.setAttribute('aria-expanded', 'false');
  document.body.classList.remove('cp-open');
  current = null;
}
addEventListener('hashchange', close);
addEventListener('resize', () => { if (current && !current.panel.classList.contains('cp-sheet')) close(); });
